import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import EmptyState from '@/components/feature/EmptyState';
import { videos } from '@/mocks/videos';
import type { Video } from '@/types/video';
import { useFavorites } from '@/hooks/useFavorites';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function FavoritesPage() {
  const { favoriteIds, removeFavorite, clearFavorites } = useFavorites();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState('');

  useDocumentTitle({
    title: '我的收藏 - 成人快手',
    description: '查看你在成人快手收藏的成人主题短视频，支持随时移除收藏。',
    keywords: '成人快手,收藏,我的收藏,短视频',
  });

  const allVideos = videos as Video[];

  const favoriteVideos = useMemo(
    () => favoriteIds.map((id) => allVideos.find((v) => v.id === id)).filter(Boolean) as Video[],
    [favoriteIds, allVideos],
  );

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2200);
  };

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '我的收藏' }]} />

      <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我的收藏</h1>
          <p className="mt-1 text-sm text-foreground-500">
            共收藏 <span className="font-semibold text-foreground-900">{favoriteVideos.length}</span> 个视频 · 收藏状态保存在本机浏览器
          </p>
        </div>
        {favoriteVideos.length > 0 && (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="flex items-center gap-2 rounded-full border border-background-300/70 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-600 hover:text-primary-300"
          >
            <i className="ri-delete-bin-6-line" />
            清空全部
          </button>
        )}
      </div>

      <div className="mt-6">
        {favoriteVideos.length === 0 ? (
          <EmptyState
            icon="ri-heart-3-line"
            title="暂无收藏视频"
            description="浏览喜欢的视频时，点击爱心就能把它收藏到这里。"
            actionLabel="去发现内容"
            actionTo="/"
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {favoriteVideos.map((v) => (
              <VideoCard
                key={v.id}
                video={v}
                showFavorite={false}
                showRemove
                onRemove={(id) => {
                  removeFavorite(id);
                  showToast('已移除该收藏');
                }}
              />
            ))}
          </div>
        )}
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setConfirmOpen(false)}
        >
          <div
            className="w-full max-w-sm animate-fade-up rounded-xl border border-background-200 bg-background-100 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500/15 text-primary-300">
                <i className="ri-error-warning-line text-xl" />
              </span>
              <h3 className="font-heading text-base font-bold text-foreground-950">清空全部收藏？</h3>
            </div>
            <p className="text-sm leading-relaxed text-foreground-600">
              清空后你将失去全部收藏记录，该操作无法撤销。
            </p>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="flex-1 rounded-full border border-background-300/70 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => {
                  clearFavorites();
                  setConfirmOpen(false);
                  showToast('已清空全部收藏');
                }}
                className="flex-1 rounded-full bg-primary-500 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400"
              >
                确认清空
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-24 left-1/2 z-[80] -translate-x-1/2 animate-fade-up rounded-full bg-background-900 px-5 py-2.5 text-sm text-foreground-950 shadow-lg lg:bottom-8">
          {toast}
        </div>
      )}

      {favoriteVideos.length > 0 && (
        <div className="mt-8 text-center">
          <Link to="/" className="text-sm text-foreground-500 transition-colors hover:text-primary-300">
            继续浏览更多内容 →
          </Link>
        </div>
      )}
    </div>
  );
}